import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { SIDEBAR_DRAWER_ID } from '../../hooks/useSidebarDrawer';
import logo from '../../assets/logo_nexus_sem_fundo.png';
import styles from './AppHeader.module.css';

/**
 * Retorna as iniciais do nome para o avatar.
 * Ex.: "Ana Paula" → "AP"
 */
function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * AppHeader — barra superior slim para a área autenticada.
 * Convive com a Sidebar existente (não tem links de navegação).
 * Height: 64px — fixada no topo, a Sidebar e o conteúdo ficam abaixo.
 *
 * @param {function} [onMenuClick] - Callback chamado ao clicar no hamburguer (mobile).
 *   Só é renderizado quando esta prop for fornecida.
 * @param {boolean} [menuExpanded=false] - Estado do drawer da Sidebar (aberto/fechado),
 *   refletido no `aria-expanded` do hamburguer para o leitor de tela.
 */
export default function AppHeader({ onMenuClick, menuExpanded = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  const displayName = user?.name || user?.email || 'Usuário';
  const initials = getInitials(displayName);

  // Fechar o menu ao clicar fora
  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  // Fechar com Esc
  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const handleMenuItemClick = useCallback((path) => {
    setMenuOpen(false);
    navigate(path);
  }, [navigate]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => !prev);
  }, []);

  return (
    <header className={styles.header} role="banner">
      <div className={styles.inner}>
        {/* Hamburger — visível apenas no mobile, e apenas quando a página passa onMenuClick */}
        {onMenuClick && (
          <button
            type="button"
            className={styles.hamburger}
            aria-label="Abrir menu de navegação"
            aria-expanded={menuExpanded}
            aria-controls={SIDEBAR_DRAWER_ID}
            onClick={onMenuClick}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}

        {/* Logo */}
        <Link to="/agenda" className={styles.logo} aria-label="Nexus Tech — ir para Agenda">
          <img src={logo} alt="" width="28" height="28" className={styles.logoImg} />
          <span className={styles.logoText}>Nexus Tech</span>
        </Link>

        {/* Menu de usuário */}
        <div className={styles.userMenu} ref={menuRef}>
          <button
            ref={triggerRef}
            type="button"
            className={styles.userTrigger}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-controls="app-user-menu"
            aria-label={`Menu do usuário: ${displayName}`}
            onClick={toggleMenu}
          >
            {/* Avatar */}
            <span className={styles.avatar} aria-hidden="true">
              {initials}
            </span>
            {/* Nome truncado */}
            <span className={styles.userName}>{displayName}</span>
            {/* Seta */}
            <span
              className={[styles.arrow, menuOpen ? styles.arrowOpen : ''].join(' ')}
              aria-hidden="true"
            >
              ▾
            </span>
          </button>

          {menuOpen && (
            <div id="app-user-menu" className={styles.dropdown} role="menu" aria-label="Opções do usuário">
              <button
                type="button"
                className={styles.menuItem}
                role="menuitem"
                onClick={() => handleMenuItemClick('/user-settings')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                </svg>
                Perfil
              </button>

              <button
                type="button"
                className={styles.menuItem}
                role="menuitem"
                onClick={() => handleMenuItemClick('/subscription/billing')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" stroke="currentColor" strokeWidth="2" />
                  <line x1="1" y1="10" x2="23" y2="10" stroke="currentColor" strokeWidth="2" />
                </svg>
                Plano e cobrança
              </button>

              <button
                type="button"
                className={styles.menuItem}
                role="menuitem"
                onClick={() => handleMenuItemClick('/business/settings')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" />
                </svg>
                Configurações
              </button>

              <hr className={styles.divider} />

              <button
                type="button"
                className={[styles.menuItem, styles.menuItemDanger].join(' ')}
                role="menuitem"
                onClick={handleLogout}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="16 17 21 12 16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
